// PII redaction patterns used on client (widget) AND server (defense in depth).
// See v3.0 review + L-059: applies to console logs, network-error URLs, AND top-level
// submission URL + referrer (query-string values get stripped).

// Patterns that strip an entire matched substring, replacing with [REDACTED:<kind>]
const PATTERNS: Array<{ kind: string; re: RegExp }> = [
  // Bearer tokens (OAuth, JWT in Authorization headers)
  { kind: 'bearer', re: /Bearer\s+[A-Za-z0-9._-]{10,}/gi },
  // JWT-shaped strings (header.payload.signature)
  { kind: 'jwt', re: /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g },
  // Long base64-ish blobs (tokens, API keys, secrets)
  { kind: 'token', re: /[A-Za-z0-9+/=]{40,}/g },
  // Email addresses (in log bodies; form fields are separate intentional capture)
  { kind: 'email', re: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g },
  // password=... and api_key=... patterns (common in log lines)
  { kind: 'secret', re: /\b(password|api[_-]?key|access[_-]?key|secret[_-]?key)\s*[=:]\s*\S+/gi },
  // Credit card–shaped numbers (Luhn-check confirmed separately)
  { kind: 'cc', re: /\b(?:\d[ -]*?){13,19}\b/g },
];

function luhnValid(s: string): boolean {
  const digits = s.replace(/\D/g, '');
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    const d = digits.charCodeAt(i) - 48;
    if (d < 0 || d > 9) return false;
    let n = d;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/**
 * Redact PII patterns in arbitrary free-text.
 * Credit-card pattern also requires Luhn validation (avoids false positives on
 * random 16-digit identifiers).
 */
export function redactText(input: string): string {
  let out = input;
  for (const { kind, re } of PATTERNS) {
    if (kind === 'cc') {
      out = out.replace(re, (m) => (luhnValid(m) ? '[REDACTED:cc]' : m));
    } else {
      out = out.replace(re, `[REDACTED:${kind}]`);
    }
  }
  return out;
}

/**
 * Redact sensitive values from a URL's query string while preserving keys.
 * Applies to submission URL, referrer, and network-error URLs.
 * Example: `?auth=xyz&email=a@b.com&page=5` → `?auth=[REDACTED]&email=[REDACTED]&page=5`
 *
 * Keys that always get their VALUE stripped:
 */
const REDACT_KEYS = new Set([
  'auth',
  'authorization',
  'token',
  'access_token',
  'refresh_token',
  'id_token',
  'api_key',
  'apikey',
  'key',
  'secret',
  'password',
  'pwd',
  'pass',
  'email',
  'user_email',
  'customer_email',
  'phone',
  'ssn',
  'session',
  'session_id',
  'sessionid',
  'jwt',
]);

export function redactUrlQueryValues(url: string): string {
  try {
    const u = new URL(url);
    for (const key of Array.from(u.searchParams.keys())) {
      const lowered = key.toLowerCase();
      if (REDACT_KEYS.has(lowered)) {
        u.searchParams.set(key, '[REDACTED]');
      } else {
        // Even for non-sensitive keys, apply pattern redaction to the VALUE
        const v = u.searchParams.get(key) ?? '';
        const redacted = redactText(v);
        if (redacted !== v) u.searchParams.set(key, redacted);
      }
    }
    return u.toString();
  } catch {
    // Not a valid URL; fall back to plain text redaction
    return redactText(url);
  }
}
